/**
 * Client-side share utility. Prefers navigator.share when available,
 * falls back to clipboard copy.
 */

export interface ShareOptions {
  url: string;
  title?: string;
  text?: string;
}

export interface ShareResult {
  success: boolean;
  method: "share" | "clipboard";
  error?: unknown;
}

function clipboardApiUnavailableError(): Error {
  const err = new Error("Clipboard API is not available");
  err.name = "NotSupportedError";
  return err;
}

function canWriteClipboard(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
}

/**
 * Share a URL using the Web Share API when available, otherwise copy to clipboard.
 * Call from client only (e.g. event handlers).
 */
export async function shareLink({ url, title, text }: ShareOptions): Promise<ShareResult> {
  const shareData: ShareData = {
    url,
    title: title ?? "",
    text: text ?? "",
  };

  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share(shareData);
      return { success: true, method: "share" };
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return { success: false, method: "share", error: err };
      }
      return { success: false, method: "share", error: err };
    }
  }

  if (!canWriteClipboard()) {
    return { success: false, method: "clipboard", error: clipboardApiUnavailableError() };
  }

  try {
    await navigator.clipboard.writeText(url);
    return { success: true, method: "clipboard" };
  } catch (err) {
    return { success: false, method: "clipboard", error: err };
  }
}

/**
 * Copy a URL to the clipboard only (does not use the Web Share API).
 * Use when the UI should always behave like a copy action.
 */
export async function copyUrlToClipboard(url: string): Promise<ShareResult> {
  if (!canWriteClipboard()) {
    return { success: false, method: "clipboard", error: clipboardApiUnavailableError() };
  }

  try {
    await navigator.clipboard.writeText(url);
    return { success: true, method: "clipboard" };
  } catch (err) {
    return { success: false, method: "clipboard", error: err };
  }
}
